import { useState } from "react";
import { Link } from "react-router-dom";
import { Landmark } from "lucide-react";

import type { Movimiento } from "../api/tipos.ts";
import { usarElegirCuenta, usarLibro } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { deDondeSalio, enLaLista, identificadorDe, llaveDe, nombreDeCuenta, rotuloDeCuenta } from "./cuenta-modelo.ts";
import { cx } from "./ui.tsx";

/**
 * A qué cuenta del negocio entró un pago, y elegirla cuando el banco no lo dice
 * (SPEC-030 §6).
 *
 * Una línea, no un bloque: es un dato del pago, no una decisión sobre él. Se
 * elige de la lista con un solo cambio del selector, y se deshace igual (P-3).
 * Lo elige todo el que decide sobre pagos, cajero incluido (D-78).
 */
export function CuentaDelPago({ movimiento }: { movimiento: Movimiento }) {
  const { data: libro } = usarLibro();
  // El pago vigente, no la copia de quien lo abrió: el detalle de Pagos guarda
  // el pago del momento del clic, y tras elegir la cuenta seguiría diciendo la
  // de antes.
  const m = libro?.movimientos.find((x) => x.clave === movimiento.clave) ?? movimiento;
  const { puedo } = usarSesion();
  const elegir = usarElegirCuenta();
  const [abierto, setAbierto] = useState(false);
  const [deshacer, setDeshacer] = useState<{ banco: string; cuenta: string } | null>(null);

  const lista = libro?.cuentasBancarias ?? [];
  const c = m.cuentaBancaria;
  const texto = nombreDeCuenta(c);
  const puedeElegir = puedo(PERMISO.DECIDIR);
  const actual = enLaLista(c, lista);

  // Lo que había antes, para deshacer: si una persona ya la había elegido, se
  // vuelve a esa; si no, a lo que diga el banco.
  const anterior = c?.origen === "persona" && c.cuenta ? { banco: c.banco, cuenta: c.cuenta } : { banco: "", cuenta: "" };

  function cambiar(llave: string) {
    const elegida = lista.find((x) => llaveDe(x) === llave);
    if (!elegida) return;
    elegir.mutate(
      { claves: [m.clave], banco: elegida.banco, cuenta: identificadorDe(elegida) },
      {
        onSuccess: () => {
          setAbierto(false);
          setDeshacer(anterior);
        },
      },
    );
  }

  return (
    <div className="space-y-1.5 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Landmark className="size-4 shrink-0 text-tenue" aria-hidden />
        <span className="text-tenue">Entró a</span>
        {texto ? (
          <span className="font-medium text-tinta">{texto}</span>
        ) : (
          <span className="rounded-full bg-aviso-suave px-2 py-0.5 text-xs font-medium text-aviso-tinta">
            Cuenta sin identificar
          </span>
        )}
        {texto && deDondeSalio(c) && <span className="text-xs text-tenue-2">· {deDondeSalio(c)}</span>}
        {puedeElegir && lista.length > 0 && !abierto && (
          <button
            type="button"
            onClick={() => setAbierto(true)}
            className="text-xs font-medium text-marca hover:underline"
          >
            {texto ? "Cambiar" : "Elegir la cuenta"}
          </button>
        )}
      </div>

      {puedeElegir && abierto && (
        <div className="flex flex-wrap items-center gap-2 pl-6">
          <label htmlFor={`cuenta-${m.clave}`} className="text-xs text-tenue">
            ¿A qué cuenta entró?
          </label>
          <select
            id={`cuenta-${m.clave}`}
            autoFocus
            defaultValue={actual ? llaveDe(actual) : ""}
            disabled={elegir.isPending}
            onChange={(e) => cambiar(e.target.value)}
            className={cx(
              "h-8 rounded-lg border border-borde bg-superficie px-2 text-sm focus:border-marca focus:outline-none",
            )}
          >
            <option value="" disabled>
              Elige una…
            </option>
            {lista.map((x) => (
              <option key={llaveDe(x)} value={llaveDe(x)}>
                {rotuloDeCuenta(x)}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => setAbierto(false)} className="text-xs text-tenue hover:text-tinta">
            Cancelar
          </button>
        </div>
      )}

      {puedeElegir && !texto && lista.length === 0 && (
        <p className="pl-6 text-xs text-tenue">
          {puedo(PERMISO.CONFIGURAR) ? (
            <>
              Para elegirla, registra tus cuentas en{" "}
              <Link to="/configuracion#cuentas" className="font-medium text-marca hover:underline">
                Configuración
              </Link>
              .
            </>
          ) : (
            "Para elegirla, el dueño tiene que registrar las cuentas del negocio en Configuración."
          )}
        </p>
      )}

      {deshacer && !abierto && (
        <p className="pl-6 text-xs text-exito-tinta">
          Listo, la cuenta quedó guardada.{" "}
          <button
            type="button"
            disabled={elegir.isPending}
            onClick={() =>
              elegir.mutate({ claves: [m.clave], ...deshacer }, { onSuccess: () => setDeshacer(null) })
            }
            className="font-medium underline"
          >
            Deshacer
          </button>
        </p>
      )}
      {elegir.error && <p className="pl-6 text-xs text-riesgo-tinta">{elegir.error.message}</p>}
    </div>
  );
}
